import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Cable } from 'lucide-react';
import {
  EmptyState,
  DatePicker,
  PageFrame,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  messageFromError,
  toast,
  unwrapAction,
  todayIso,
} from '@eo/shared';

import { REVIEW_TABS, REVIEW_TAB_LABELS, SETUP_COPY } from './constants';
import {
  clientChangePatch,
  groupIntoMeetings,
  groupOverdueByDate,
  isInteractiveTarget,
  meetingHeading,
  overdueDates,
  splitByDay,
  toApproveBody,
  toDraft,
  toRejectBody,
  undoInProgressMessage,
  failedAttachmentsNotice,
} from './helpers';
import { MeetingsProcessed } from './MeetingsProcessed';
import { OverdueTab } from './OverdueTab';
import { TaskEditorDialog } from './TaskEditorDialog';
import { TodaysDecisions } from './TodaysDecisions';
import { UndoInProgressDialog } from './UndoInProgressDialog';
import { TaskCard } from './TaskCard';
import type { HistoryEntry } from './api/types';
import type { Decision, ReviewTab, TaskDraft, ReviewBoardBodyProps } from './types';

export function ReviewBoardBody({
  data,
  onApprove,
  onReject,
  onRevert,
  onLoadMembers,
  onQueryOptions,
}: ReviewBoardBodyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drafts, setDrafts] = useState<TaskDraft[]>([]);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [undoInProgress, setUndoInProgress] = useState<{
    entry: HistoryEntry;
    message: string;
  } | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);

  const today = todayIso();
  const tab: ReviewTab = searchParams.get('tab') === 'overdue' ? 'overdue' : 'day-board';

  // View-state reconciliation: whenever the fetched queue changes (date
  // switch, refetch, post-decision removal) keep the CFO's in-progress edits
  // on cards that are still present and only build fresh drafts for new tasks.
  useEffect(() => {
    setDrafts((prev) =>
      data.tasks.map((task) => prev.find((d) => d.taskId === task.taskId) ?? toDraft(task)),
    );
  }, [data.tasks]);

  const { onTheDay, overdue } = useMemo(
    () => splitByDay(drafts, data.date, today),
    [drafts, data.date, today],
  );

  const meetings = useMemo(() => groupIntoMeetings(onTheDay), [onTheDay]);
  const overdueGroups = useMemo(() => groupOverdueByDate(overdue, today), [overdue, today]);
  const markedDates = useMemo(() => overdueDates(drafts, today), [drafts, today]);

  // Keyboard order follows reading order, and only the visible tab takes part —
  // a shortcut must never act on a card the CFO cannot see.
  const navigable = useMemo(
    () =>
      tab === 'overdue'
        ? overdueGroups.flatMap((g) => g.drafts.map((d) => d.taskId))
        : meetings.flatMap((m) => m.drafts.map((d) => d.taskId)),
    [tab, overdueGroups, meetings],
  );

  const decide = useCallback(
    async (draft: TaskDraft, decision: Decision) => {
      if (busyTaskId) return;
      setBusyTaskId(draft.taskId);
      try {
        if (decision === 'approve') {
          if (!draft.clientId) {
            throw new Error(
              `No ClickUp list is configured for "${draft.client}". Change the client under Details, or reject it.`,
            );
          }
          // Filed on the meeting's day, not the day it was clicked: a task
          // decided from the overdue tab belongs to the meeting it came from,
          // and its history has to land there too.
          const approved = unwrapAction<{ failedAttachments: string[] }>(
            await onApprove(toApproveBody(draft, draft.meetingDate)),
          );
          const notice = failedAttachmentsNotice(approved.failedAttachments);
          if (notice) toast.warning(notice);
        } else {
          unwrapAction(await onReject(toRejectBody(draft, draft.meetingDate)));
        }
      } catch (err) {
        toast.error(messageFromError(err, 'Something went wrong.'));
      } finally {
        setBusyTaskId(null);
      }
    },
    [busyTaskId, onApprove, onReject],
  );

  const revert = useCallback(
    async (entry: HistoryEntry, confirmed = false) => {
      if (revertingId || !entry.taskId) return;
      setRevertingId(entry.id);
      try {
        const result = await onRevert({
          historyId: entry.id,
          taskId: entry.taskId,
          date: data.date,
          confirmed,
        });
        const inProgress = confirmed ? null : undoInProgressMessage(result);
        if (inProgress) {
          setUndoInProgress({ entry, message: inProgress });
          return;
        }
        unwrapAction(result);
        setUndoInProgress(null);
      } catch (err) {
        setUndoInProgress(null);
        toast.error(messageFromError(err, 'Something went wrong.'));
      } finally {
        setRevertingId(null);
      }
    },
    [revertingId, onRevert, data.date],
  );

  // A queue is worked with two fingers: move, decide, move on. The editor is
  // modal, so while it is open every key belongs to it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (editingTaskId !== null) return;
      if (isInteractiveTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey)
        return;
      if (navigable.length === 0) return;

      const current = selectedTaskId ? navigable.indexOf(selectedTaskId) : -1;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next =
          current === -1 ? 0 : Math.min(Math.max(current + step, 0), navigable.length - 1);
        setSelectedTaskId(navigable[next]);
        return;
      }

      const approve = event.key === 'Enter';
      const reject = event.key === 'Delete' || event.key === 'Backspace';
      if (!approve && !reject) return;
      if (selectedTaskId === null) return;
      const draft = drafts.find((d) => d.taskId === selectedTaskId);
      if (!draft) return;
      event.preventDefault();
      void decide(draft, approve ? 'approve' : 'reject');
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [decide, drafts, editingTaskId, navigable, selectedTaskId]);

  const patchDraft = (taskId: string, patch: Partial<TaskDraft>) => {
    // A client change re-routes the card: swap the ClickUp routing fields as a
    // unit and drop the parent pick (it belongs to the old client's list).
    const enriched =
      typeof patch.client === 'string' && typeof patch.clientId === 'string'
        ? clientChangePatch({ id: patch.clientId, name: patch.client })
        : patch;
    setDrafts((prev) => prev.map((d) => (d.taskId === taskId ? { ...d, ...enriched } : d)));
  };

  const onDateChange = (value: string) => {
    if (!value) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('date', value);
      return next;
    });
  };

  const onTabChange = (next: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'day-board') params.delete('tab');
      else params.set('tab', next);
      return params;
    });
  };

  const requestMembers = (draft: TaskDraft) => {
    if (!draft.clientId || data.membersByClient[draft.clientId]) return;
    onLoadMembers({ clientId: draft.clientId });
  };

  if (data.setupRequired) {
    const copy = SETUP_COPY[data.setupRequired];
    return (
      <div className="flex flex-col">
        <PageFrame title="Review Board" />
        <div className="px-[var(--page-margin)]">
          <EmptyState
            icon={Cable}
            title={copy.title}
            description={copy.description}
            action={
              data.setupRequired === 'clients' ? undefined : (
                <Link
                  to="/settings?tab=integrations"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Open Settings
                </Link>
              )
            }
          />
        </div>
      </div>
    );
  }

  const editingDraft = drafts.find((d) => d.taskId === editingTaskId) ?? null;

  return (
    // The frame sits inside <Tabs> so the tab list can live in its control row
    // while the panels stay under the same Tabs context.
    <Tabs value={tab} onValueChange={onTabChange} className="flex flex-col">
      <PageFrame
        title="Review Board"
        subtitle="Decide what the pipeline proposed from your meetings. Anything you leave alone waits for you."
        badge={
          <span className="text-xs text-text-tertiary">
            {`${onTheDay.length} task${onTheDay.length === 1 ? '' : 's'} from ${meetings.length} meeting${meetings.length === 1 ? '' : 's'}`}
          </span>
        }
        controls={
          // Picker first and alone on its line, tabs under it: the date scopes
          // the day board, so it reads before the thing it scopes.
          <div className="flex flex-col items-start gap-3">
            <DatePicker
              value={searchParams.get('date') ?? data.date}
              onChange={onDateChange}
              className="w-40"
              ariaLabel="Review date"
              maxDate={today}
              markedDates={markedDates}
              markVariant="attention"
            />
            {/* The log rides with the tabs rather than sitting under the
                board: it is confirmation, not work, and at the foot it either
                pushed the queue down or stranded itself below an empty state. */}
            <div className="flex flex-wrap items-center gap-6">
              <TabsList variant="underline">
                {REVIEW_TABS.map((name) => (
                  <TabsTrigger key={name} value={name}>
                    {REVIEW_TAB_LABELS[name]}
                    {name === 'overdue' && overdue.length > 0 ? ` (${overdue.length})` : ''}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TodaysDecisions
                entries={data.history}
                busyId={revertingId}
                onRevert={(entry) => void revert(entry)}
              />
              <MeetingsProcessed meetings={data.meetings} />
              {undoInProgress && (
                <UndoInProgressDialog
                  taskName={undoInProgress.entry.taskName}
                  message={undoInProgress.message}
                  busy={revertingId !== null}
                  onCancel={() => setUndoInProgress(null)}
                  onConfirm={() => void revert(undoInProgress.entry, true)}
                />
              )}
            </div>
          </div>
        }
      />

      <TabsContent value="overdue" className="flex flex-col gap-6 pb-8">
        <div className="px-[var(--page-margin)]">
          <OverdueTab
            groups={overdueGroups}
            busyTaskId={busyTaskId}
            selectedTaskId={selectedTaskId}
            onDecide={decide}
            onEdit={setEditingTaskId}
            onSelect={setSelectedTaskId}
          />
        </div>
      </TabsContent>

      <TabsContent value="day-board" className="flex flex-col gap-6 pb-8">
        {onTheDay.length === 0 ? (
          <div className="px-[var(--page-margin)]">
            {data.history.length > 0 ? (
              <EmptyState
                title="All clear for this date"
                description="Every proposed task for this date has been decided — the history below shows what was cleared."
              />
            ) : (
              <EmptyState
                title="Nothing to review"
                description="No proposed tasks are waiting for this date. Meetings processed by the pipeline will appear here."
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-6 px-[var(--page-margin)]">
            {meetings.map((meeting) => {
              const heading = meetingHeading(meeting);

              return (
                <section
                  key={`${meeting.meetingDate}-${meeting.meetingTitle}`}
                  className="flex flex-col gap-2"
                >
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-sm font-semibold text-text-primary">{heading.title}</h2>
                    <span className="text-xs text-text-tertiary">{heading.meta}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {meeting.drafts.map((draft) => (
                      <TaskCard
                        key={draft.taskId}
                        draft={draft}
                        busy={busyTaskId === draft.taskId}
                        selected={selectedTaskId === draft.taskId}
                        disabled={busyTaskId !== null && busyTaskId !== draft.taskId}
                        onDecide={(decision) => decide(draft, decision)}
                        onEdit={() => setEditingTaskId(draft.taskId)}
                        onSelect={() => setSelectedTaskId(draft.taskId)}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </TabsContent>

      <TaskEditorDialog
        draft={editingDraft}
        options={data.options}
        members={editingDraft?.clientId ? data.membersByClient[editingDraft.clientId] : undefined}
        onChange={(patch) => {
          if (editingTaskId) patchDraft(editingTaskId, patch);
        }}
        onClose={() => setEditingTaskId(null)}
        onOpenMembers={() => editingDraft && requestMembers(editingDraft)}
        onQueryOptions={onQueryOptions}
      />
    </Tabs>
  );
}
